import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Loader2, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';

const ScanUsedVehiclePage = () => {
  const { sessionId } = useParams();
  const [status, setStatus] = useState('ready');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus('uploading');
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      setStatus('processing');
      await axios.post(`${API}/used-vehicles/scan-sessions/${sessionId}/upload`, formData);
      setStatus('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du scan');
      setStatus('error');
    }
  };

  const retry = () => {
    setStatus('ready');
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-3">
            <span className="text-white font-bold text-xl">V</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Vortekio</h1>
          <p className="text-sm text-slate-500 mt-1">Scanner le permis de circulation</p>
        </div>

        {status === 'ready' && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-blue-300 rounded-2xl p-8 text-center bg-white cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all active:scale-95"
              onClick={() => fileInputRef.current?.click()}
              data-testid="capture-area"
            >
              <Camera className="w-16 h-16 mx-auto text-blue-500 mb-4" />
              <p className="text-lg font-semibold text-slate-800">Prendre la photo</p>
              <p className="text-sm text-slate-500 mt-1">Appuyez pour ouvrir l'appareil photo</p>
            </div>
            <input
              ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
              capture="environment" className="hidden" onChange={handleCapture}
              data-testid="scan-file-input"
            />
            <p className="text-xs text-center text-slate-400">
              Positionnez le permis de circulation bien a plat et assurez-vous que tous les champs sont visibles
            </p>
          </div>
        )}

        {(status === 'uploading' || status === 'processing') && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-200">
            <Loader2 className="w-16 h-16 mx-auto text-blue-500 animate-spin mb-4" />
            <p className="text-lg font-semibold text-slate-800">
              {status === 'uploading' ? 'Envoi de la photo...' : 'Analyse en cours...'}
            </p>
            <p className="text-sm text-slate-500 mt-1">L'IA lit les données du permis de circulation</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-emerald-200">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <p className="text-lg font-semibold text-slate-800">Scan réussi !</p>
            <p className="text-sm text-slate-500 mt-1">
              Les données ont été envoyées au formulaire.<br />Vous pouvez fermer cette page.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-red-200">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <p className="text-lg font-semibold text-slate-800">Erreur</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <Button onClick={retry} className="mt-4" variant="outline" data-testid="retry-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Réessayer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanUsedVehiclePage;
